import { useAccount } from '@/features/account'
import { useEnv } from '@/shared/config'
import { pushToDataLayer } from '@/shared/lib/analytics/gtm'
import type { ExportTelemetrySink } from '@/shared/lib/export'

// The GTM half of the export adapter's `download_triggered` event (ORP-20213, contract 16).
// Lives here for the same reason `useRouteAnalytics` does: the production/`GTM_ID` gate belongs at
// the call site, not in the `pushToDataLayer` util, and only the composition root may read both
// module 07's user id and the env.
//
// v3 emitted this from inside `downloadXLSX` (`composables/format.js:1402-1414`). The payload keys,
// the `event_type: 'Optional'` marker and the user-id gate are that event verbatim; what the port
// changes is where `file_format` and `feature_name` come from — the caller's `telemetry` option,
// rather than `layoutStore.currentFeatureName`, which v4 has no equivalent of.

/** True only in production with a configured GTM container — otherwise analytics no-op. */
function analyticsEnabled(): boolean {
  const env = useEnv()
  return env.MODE === 'production' && env.GTM_ID !== undefined && env.GTM_ID !== ''
}

export function useExportTelemetry(): ExportTelemetrySink {
  return (event) => {
    if (!analyticsEnabled()) return
    // v3 pushed nothing without a user id (`if (accountStore.userId)`); an anonymous download is
    // not a measurable one.
    const userId = event.userId ?? useAccount().userId.value
    if (userId === undefined || userId === '') return

    pushToDataLayer({
      event: 'download_triggered',
      user_id: userId,
      file_format: event.fileFormat,
      feature_name: event.featureName ?? '',
      event_type: 'Optional',
    })
  }
}
